import * as yup from 'yup';
import { DocumentNode } from 'graphql';
import { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-form-state-manager';
import { useModal } from 'react-modal-hook';
import { useTranslation } from 'react-i18next';

import {
  GetLotteriesForEventQuery as GetLotteriesForEvent,
  GetRegistrationSummaryQuery as GetRegistrationSummary,
} from '__generated__/graphql';

import { getServerErrors } from '../../../../common/helpers';
import { useNotifier } from '../../../../common/Notifications/NotificationProvider';
import DateRangeForm, { dateRangeObject } from '../../../Common/DateRangeForm';
import EditRegistrationBackendMutation from './EditRegistrationBackendMutation';
import GetLotteriesForEventQuery from '../../Lotteries/GetLotteriesForEventQuery';
import GetParticipantDataQuery from '../GetParticipantDataQuery';
import GetRegistrationSummaryQuery from './GetRegistrationSummaryQuery';
import RegistrationInfo from './RegistrationInfo';
import UI from '../../../../common/UI';
import useLocale from '../../../../common/useLocale';
import useMutation from '../../../../api/useMutation';
import useProject from '../../../useProject';
import useQuery from '../../../../api/useQuery';
import useValidation from '../../../../common/useValidation';

type Registration = GetRegistrationSummary['registrationSummary']['registrations'][0];
type Lottery = GetLotteriesForEvent['project']['event']['lotteries'][0];

const getSetDrawResultSchema = (requireDates: boolean) => yup.object().shape(
  requireDates ? dateRangeObject(true, true) : {},
);

export interface EditDrawResultFormProps {
  registration: Registration;
  lotteries: Lottery[];
  onSuccess: () => void;
  refetchQueries?: DocumentNode[];
}

export const EditDrawResultForm = ({
  registration, lotteries, onSuccess, refetchQueries = [GetParticipantDataQuery, GetRegistrationSummaryQuery],
}: EditDrawResultFormProps) => {
  const { t } = useTranslation();
  const notifier = useNotifier();
  const { parseDate, formatDate } = useLocale();

  const getValues = (registration: Registration) => ({
    lottery_id: registration.lottery?.id,
    overwrite_confirmation_period: !!registration.preregistration.lottery_start_date
      && !!registration.preregistration.lottery_end_date,
    start_date: registration.preregistration.lottery_start_date,
    end_date: registration.preregistration.lottery_end_date,
    rejected_lotteries: registration.rejected_lotteries.map((lottery) => lottery.id),
  });

  const form = useForm({
    values: getValues(registration),
  });

  const lottery = lotteries.find((lottery) => lottery.id === form.values.lottery_id);

  const [editRegistration, { error, loading }] = useMutation(
    EditRegistrationBackendMutation,
    {
      variables: {
        input: {
          id: registration.id,
          lottery_details: {
            lottery_id: form.values.lottery_id,
            rejected_lotteries: form.values.rejected_lotteries.filter(
              (lotteryId) => lotteryId !== form.values.lottery_id,
            ),
            start_date: form.values.lottery_id && form.values.overwrite_confirmation_period
              ? form.values.start_date
              : null,
            end_date: form.values.lottery_id && form.values.overwrite_confirmation_period
              ? form.values.end_date
              : null,
          },
        },
      },
      refetchQueries,
      awaitRefetchQueries: true,
      onCompleted: () => {
        notifier.success(t('notifications.registration_saved'));
        onSuccess();
      },
    },
  );

  const { valid, errors } = useValidation({
    schema: getSetDrawResultSchema(form.values.lottery_id && form.values.overwrite_confirmation_period),
    values: form.values,
    externalErrors: getServerErrors(error),
  });

  const datePlaceholders = lottery?.start_date && lottery?.end_date ? {
    startDate: formatDate(parseDate(lottery.start_date, { timezone: 'UTC' }), { format: 'input_date' }),
    startTime: formatDate(parseDate(lottery.start_date, { timezone: 'UTC' }), { format: 'input_time' }),
    endDate: formatDate(parseDate(lottery.end_date, { timezone: 'UTC' }), { format: 'input_date' }),
    endTime: formatDate(parseDate(lottery.end_date, { timezone: 'UTC' }), { format: 'input_time' }),
  } : null;

  return (
    <UI.Form onSubmit={() => editRegistration()}>
      <UI.FormGrid>
        <UI.Div>
          <UI.H4 sc={{ mb: 2 }}>
            {t('edit_individual_draw_result') }
          </UI.H4>
          <UI.Paragraph sc={{ mb: 0 }}>
            {t('edit_individual_draw_result_description')}
          </UI.Paragraph>
        </UI.Div>

        <RegistrationInfo registration={registration} />

        <UI.ServerErrors error={error} />

        <UI.InputGroup sc={{ valid: !errors.lottery, touched: form.getTouched('lottery') }}>
          <UI.InputLabel htmlFor="RegistrationSelectedLottery">
            {t('selected_by_lottery')}
          </UI.InputLabel>
          <UI.Select
            {...form.select('lottery_id', [null, ...lotteries.map((lottery) => lottery.id)])}
            id="RegistrationSelectedLottery"
          >
            <option value={0}>
              {t('none')}
            </option>
            {lotteries.map((lottery, index) => (
              <option {...form.option(index + 1)} key={index}>
                {lottery.title}
              </option>
            ))}
          </UI.Select>
          <UI.ErrorMessages attribute={t('selected_by_lottery')} errors={errors.lottery} />
        </UI.InputGroup>

        {form.values.lottery_id && (
          <>
            <UI.InputGroup>
              <UI.InputLabel htmlFor="RegistrationLotteryConfirmationPeriod">
                {t('confirmation_period')}
              </UI.InputLabel>
              <UI.Checkbox
                {...form.checkbox('overwrite_confirmation_period')}
                id="RegistrationLotteryConfirmationPeriod"
              >
                {t('set_custom_confirmation_period')}
              </UI.Checkbox>
            </UI.InputGroup>

            {form.values.overwrite_confirmation_period && (
              <DateRangeForm
                form={form}
                errors={errors}
                placeholders={datePlaceholders}
                requireStart
                requireEnd
              />
            )}
          </>
        )}

        {(!form.values.lottery_id || lotteries.length > 1) && (
          <UI.InputGroup sc={{ valid: !errors.rejected_lotteries, touched: form.getTouched('rejected_lotteries') }}>
            <UI.InputLabel htmlFor="RegistrationRejectedLotteries_0">
              {t('rejected_by_lotteries')}
            </UI.InputLabel>
            {lotteries.map((lottery, index) => (
              <UI.Checkbox
                {...form.checklist('rejected_lotteries', lottery.id)}
                {...lottery.id === form.values.lottery_id ? { checked: false } : undefined}
                id={`RegistrationRejectedLotteries_${index}`}
                disabled={lottery.id === form.values.lottery_id}
                key={lottery.id}
              >
                {lottery.title}
              </UI.Checkbox>
            ))}
            <UI.ErrorMessages attribute={t('rejected_by_lotteries')} errors={errors.rejected_lotteries} />
          </UI.InputGroup>
        )}

        <UI.HR />

        <UI.Div sc={{ textAlign: 'right' }}>
          <UI.Button
            type="submit"
            disabled={!form.changed() || !valid || loading}
            sc={{ brand: 'secondary', width: ['100%', 'auto'], loading }}
          >
            {t('confirm')}
          </UI.Button>
        </UI.Div>
      </UI.FormGrid>
    </UI.Form>
  );
};

interface UseEditDrawResultModalProps {
  registration: Registration;
  refetchQueries?: DocumentNode[];
}

const useEditDrawResultModal = ({
  registration, refetchQueries,
}: UseEditDrawResultModalProps) => {
  const project = useProject();

  /**
   * Used to prevent state updates on an unmounted component.
   * When a registration is assigned away, the <RegistrationDetails /> component is unmounted.
   * If we don't check for this, React may throw an error.
   */
  const mountedRef = useRef(false);

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  });

  const [open, setOpen] = useState(false);

  const { data, refetch } = useQuery(GetLotteriesForEventQuery, {
    variables: {
      project_id: project.id,
      event_id: registration.event.id,
    },
    skip: !open,
  });

  const lotteries = data?.project.event.lotteries;

  const [doOpenModal, closeModal] = useModal(() => {
    const handleSuccess = () => {
      if (mountedRef.current) {
        closeModal();
      }
    };

    return (
      <UI.Modal maxWidth={600} close={closeModal}>
        {!lotteries && <UI.PageLoader />}

        {lotteries && (
          <EditDrawResultForm
            registration={registration}
            lotteries={lotteries}
            refetchQueries={refetchQueries}
            onSuccess={handleSuccess}
          />
        )}
      </UI.Modal>
    );
  }, [registration, lotteries, refetchQueries]);

  const openModal = () => {
    setOpen(true);
    doOpenModal();
    refetch();
  };

  return [openModal, closeModal];
};

export default useEditDrawResultModal;
